import { useCallback, useEffect, useState } from "react";
import type { HostingVerifyResult, HostingView, RcaClient } from "@rca/sdk";

/**
 * 置き場所の状態を扱う。
 *
 * ★ **`null` は「まだ分からない」。** 「まだ選んでいない」は
 * `view.hosting === null` で表す。混ぜると、読み込み中に選択画面が
 * ちらつく（そして押せてしまう）。
 */
export interface HostingState {
  /** まだ分からなければ null。 */
  readonly view: HostingView | null;
  readonly verify: HostingVerifyResult | null;
  readonly busy: boolean;
  readonly choose: (cloud: string, target?: Readonly<Record<string, string>>) => Promise<void>;
  readonly check: () => Promise<void>;
  readonly reload: () => Promise<void>;
}

/**
 * @param tenantId いま働いているテナント。まだ分からなければ `null`。
 *
 * ★ **真偽ではなくテナントを渡す。** 以前は「ログイン済みか」だけを
 * 見ていたので、テナントを切り替えても**前のテナントの置き場所**が
 * 残った。前が使える状態だと選択画面が出ず、まだ選んでいない新しい
 * テナントで「デプロイ先を選べない」ことになる（本番で踏んだ）。
 */
export function useHosting(client: RcaClient, tenantId: string | null): HostingState {
  const [view, setView] = useState<HostingView | null>(null);
  const [verify, setVerify] = useState<HostingVerifyResult | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setView(await client.hosting.get());
    } catch {
      /**
       * ★ **取れなかったら「制限しない」に倒す。**
       *
       * 各クラウドのデプロイにはこの口が生えていない（1 テナント専用なので
       * 要らない）。そこで 404 を「まだ選んでいない」と読むと、
       * **使えている画面が選択画面に化ける**。
       */
      setView({ hosting: null, clouds: [], enrollment: null });
    }
  }, [client]);

  useEffect(() => {
    if (tenantId === null) return;
    /**
     * ★ **切り替えの途中は「まだ分からない」に戻す。**
     *
     * 前のテナントの答えを残したまま引き直すと、その一瞬だけ
     * 前の置き場所で描かれる（使える状態なら選択画面が出ない）。
     */
    setView(null);
    setVerify(null);
    void reload();
  }, [tenantId, reload]);

  const choose = useCallback(
    async (cloud: string, target?: Readonly<Record<string, string>>) => {
      setBusy(true);
      try {
        // ★ 識別子はそのまま通す。ここで組み立てると、画面が名乗った
        // 欄と食い違う（欄の出どころはアダプタの `requiredTarget`）。
        setView(await client.hosting.claim(cloud, target));
        setVerify(null);
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  const check = useCallback(async () => {
    setBusy(true);
    try {
      setVerify(await client.hosting.verify());
      // ★ 確かめたら状態が動く。取り直さないと画面が古いままになる。
      await reload();
    } finally {
      setBusy(false);
    }
  }, [client, reload]);

  return { view, verify, busy, choose, check, reload };
}

/**
 * 置き場所が決まっていないので、選択画面だけを出すべきか。
 *
 * ★ **判定を 1 か所に置く。** 画面のあちこちで書くと、1 つだけ
 * 緩いところができる（そこから「作れても動かないもの」が作られる）。
 *
 * ★ **`clouds` が空なら制限しない。** 口が生えていないデプロイ
 *（＝各クラウドの 1 テナント専用の側）では、そもそも選ぶ話が無い。
 */
export function mustChooseHosting(view: HostingView | null): boolean {
  if (view === null) return false;
  if (view.clouds.length === 0) return false;
  return view.hosting === null || view.hosting.status !== "ready";
}
